import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { supabase } from '../../lib/supabase';
import { moveToTrash } from '../../lib/trash';
import { colors, neu, radii, fonts } from '../../domain/tokens';

// ---------------------------------------------------------------------------
// PROJECTS — native port of the web "projects" management table
// (src/app/projects/page.tsx). Same `projects` table, same columns, same
// header-cell/task-sync behavior, same ETC-based projected-completion math.
//
// Web is a wide draggable table per project (rows = phases, columns =
// components). Native adaptation: a scrollable list of project cards, each
// expandable to reveal its header cells, a phase/component grid (tap to
// cycle status, long-press for a quick actions sheet), milestones, status
// updates + form, and ▲▼ buttons for project reordering (instead of
// drag-and-drop, which doesn't translate well to touch on a table this
// dense).
//
// This file is intentionally self-contained (no shared dialogs) so it can be
// dropped into an ASTRS hub tool by another concurrently-built file.
// ---------------------------------------------------------------------------

// ── Types (mirrors src/interfaces/settings.ts ProjectData / ComponentItem /
// PhaseItem / Milestone / StatusUpdate 1:1) ──

type ComponentStatus = 'active' | 'upcoming' | 'complete' | 'inactive';

interface ComponentItem {
  id: string;
  name: string;
  status: ComponentStatus;
  notes?: string;
  appliedLabelId?: string | null;
  etc?: string;
}

interface PhaseItem {
  id: string;
  components: ComponentItem[];
}

interface Milestone {
  id: string;
  label: string;
  date: string; // ISO date string
  color: string;
  notes?: string;
  isComplete: boolean;
}

interface StatusUpdate {
  id: string;
  text: string;
  date: string; // ISO date string
}

interface ProjectData {
  id: string;
  userId: string;
  activeTaskSummary: string;
  topHeaderCell1AppliedLabelId?: string | null;
  topHeaderCell2?: string;
  topHeaderCell2AppliedLabelId?: string | null;
  topHeaderCell3?: string;
  topHeaderCell3AppliedLabelId?: string | null;
  topHeaderCell4?: string;
  topHeaderCell4AppliedLabelId?: string | null;
  phases: PhaseItem[];
  showTableBody: boolean;
  showMilestones: boolean;
  createdAt?: string;
  updatedAt?: string;
  formAnswers?: Record<string, string>;
  order?: number;
  milestones?: Milestone[];
  statusUpdates?: StatusUpdate[];
}

interface ProjectComponentLabel {
  id: string;
  name: string;
  colorClass: string; // e.g. 'bg-blue-200' or '#RRGGBB'
  fontSize?: string;
  fontColorClass?: string;
}

interface ProjectFormPrompt {
  id: string;
  text: string;
}

interface TaskListMeta {
  id: string;
  name: string;
}

const SUPABASE_PROJECTS_TABLE = 'projects';
const SUPABASE_TASKS_TABLE = 'tasks';
const ASSIGNED_TASK_LIST_ID_KEY = '_assignedTaskListId';
const TIME_PER_WEEK_KEY = '_timePerWeek';

const generateId = () => `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

const createDefaultComponent = (defaultAppliedLabelId?: string | null): ComponentItem => ({
  id: generateId(),
  name: '',
  status: 'inactive',
  notes: '',
  appliedLabelId: defaultAppliedLabelId || null,
  etc: '',
});

const createInitialProjectData = (
  userId: string,
  defaultAppliedLabelId: string | null | undefined,
  order: number
): Omit<ProjectData, 'id' | 'createdAt' | 'updatedAt'> => ({
  userId,
  activeTaskSummary: '',
  topHeaderCell1AppliedLabelId: defaultAppliedLabelId || null,
  topHeaderCell2: '',
  topHeaderCell2AppliedLabelId: defaultAppliedLabelId || null,
  topHeaderCell3: '',
  topHeaderCell3AppliedLabelId: defaultAppliedLabelId || null,
  topHeaderCell4: '',
  topHeaderCell4AppliedLabelId: defaultAppliedLabelId || null,
  phases: [
    { id: `phase-1-${generateId()}`, components: Array(4).fill(null).map(() => createDefaultComponent(defaultAppliedLabelId)) },
    { id: `phase-2-${generateId()}`, components: Array(4).fill(null).map(() => createDefaultComponent(defaultAppliedLabelId)) },
    { id: `phase-3-${generateId()}`, components: Array(4).fill(null).map(() => createDefaultComponent(defaultAppliedLabelId)) },
  ],
  showTableBody: true,
  showMilestones: true,
  order,
  milestones: [],
  statusUpdates: [],
});

// Web cycles status via explicit label-menu selection in a right-click
// context menu; the closest 1:1 touch affordance is tap-to-cycle through the
// same 4 states in the same order the web's statusColors record declares.
const STATUS_CYCLE: ComponentStatus[] = ['inactive', 'upcoming', 'active', 'complete'];

const STATUS_COLORS: Record<ComponentStatus, { bg: string; text: string; label: string }> = {
  active: { bg: 'rgba(34,197,94,0.22)', text: '#166534', label: 'Active' },
  upcoming: { bg: 'rgba(234,179,8,0.22)', text: '#854d0e', label: 'Upcoming' },
  complete: { bg: 'rgba(59,130,246,0.22)', text: '#1e40af', label: 'Complete' },
  inactive: { bg: 'rgba(100,116,139,0.16)', text: '#334155', label: 'Inactive' },
};

// Approximate hex for the small fixed set of Tailwind bg-*-200/300 classes the
// web's default project component labels ship with (settings-context.tsx
// defaultAppSettings.projectComponentLabels). Custom labels created on web
// may use other Tailwind classes or raw hex; anything unmapped falls back to
// a neutral label chip so it never renders invisible/broken.
const TAILWIND_BG_TO_HEX: Record<string, string> = {
  'bg-blue-100': '#dbeafe',
  'bg-blue-200': '#bfdbfe',
  'bg-yellow-100': '#fef9c3',
  'bg-yellow-200': '#fef08a',
  'bg-green-100': '#dcfce7',
  'bg-green-200': '#bbf7d0',
  'bg-red-100': '#fee2e2',
  'bg-red-200': '#fecaca',
  'bg-purple-100': '#f3e8ff',
  'bg-purple-200': '#e9d5ff',
  'bg-gray-100': colors.glassFillStrong,
  'bg-gray-200': colors.glassFill,
  'bg-orange-100': '#ffedd5',
  'bg-orange-200': '#fed7aa',
  'bg-pink-100': '#fce7f3',
  'bg-pink-200': '#fbcfe8',
};

const TAILWIND_TEXT_TO_HEX: Record<string, string> = {
  'text-blue-800': '#1e40af',
  'text-yellow-700': '#a16207',
  'text-green-800': '#166534',
  'text-red-800': '#991b1b',
  'text-purple-800': '#6b21a8',
  'text-gray-800': '#1f2937',
  'text-orange-800': '#9a3412',
  'text-pink-800': '#9d174d',
};

function resolveClassOrHex(raw: string | undefined, map: Record<string, string>, fallback: string): string {
  if (!raw) return fallback;
  if (raw.startsWith('#') || raw.startsWith('rgb')) return raw;
  return map[raw] ?? fallback;
}

// Helper: camelCase ProjectData -> snake_case Supabase row. Mirrors web's
// toSnakeCase exactly, key for key.
function toSnakeCase(data: Partial<ProjectData> & { userId?: string }): Record<string, any> {
  const result: Record<string, any> = {};
  const keyMap: Record<string, string> = {
    userId: 'user_id',
    activeTaskSummary: 'active_task_summary',
    topHeaderCell1AppliedLabelId: 'top_header_cell1_applied_label_id',
    topHeaderCell2: 'top_header_cell2',
    topHeaderCell2AppliedLabelId: 'top_header_cell2_applied_label_id',
    topHeaderCell3: 'top_header_cell3',
    topHeaderCell3AppliedLabelId: 'top_header_cell3_applied_label_id',
    topHeaderCell4: 'top_header_cell4',
    topHeaderCell4AppliedLabelId: 'top_header_cell4_applied_label_id',
    showTableBody: 'show_table_body',
    showMilestones: 'show_milestones',
    formAnswers: 'form_answers',
    statusUpdates: 'status_updates',
    createdAt: 'created_at',
    updatedAt: 'updated_at',
  };
  for (const [key, value] of Object.entries(data)) {
    if (key === 'id') continue;
    const snakeKey = keyMap[key] || key;
    result[snakeKey] = value;
  }
  return result;
}

// Helper: snake_case Supabase row -> camelCase ProjectData. Mirrors web's
// toCamelCase exactly.
function toCamelCase(row: Record<string, any>): ProjectData {
  return {
    id: row.id,
    userId: row.user_id,
    activeTaskSummary: row.active_task_summary ?? '',
    topHeaderCell1AppliedLabelId: row.top_header_cell1_applied_label_id ?? null,
    topHeaderCell2: row.top_header_cell2 ?? '',
    topHeaderCell2AppliedLabelId: row.top_header_cell2_applied_label_id ?? null,
    topHeaderCell3: row.top_header_cell3 ?? '',
    topHeaderCell3AppliedLabelId: row.top_header_cell3_applied_label_id ?? null,
    topHeaderCell4: row.top_header_cell4 ?? '',
    topHeaderCell4AppliedLabelId: row.top_header_cell4_applied_label_id ?? null,
    phases: row.phases ?? [],
    showTableBody: row.show_table_body ?? true,
    showMilestones: row.show_milestones ?? true,
    order: row.order ?? 0,
    milestones: row.milestones ?? [],
    statusUpdates: row.status_updates ?? [],
    formAnswers: row.form_answers ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function parseEtcToHours(etcString: string | undefined): number {
  if (!etcString) return 0;
  const cleaned = etcString.toLowerCase().trim();

  const dayMatch = cleaned.match(/(\d+(\.\d+)?)\s*d/);
  if (dayMatch) return parseFloat(dayMatch[1]) * 8;

  const hourMatch = cleaned.match(/(\d+(\.\d+)?)\s*h/);
  if (hourMatch) return parseFloat(hourMatch[1]);

  const minMatch = cleaned.match(/(\d+(\.\d+)?)\s*m/);
  if (minMatch) return parseFloat(minMatch[1]) / 60;

  const numMatch = cleaned.match(/^(\d+(\.\d+)?)$/);
  if (numMatch) return parseFloat(numMatch[1]);

  return 0;
}

const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addWeeks(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n * 7);
  return copy;
}

// Sunday-start end-of-week, matching web's endOfWeek(date, { weekStartsOn: 0 }).
function endOfWeekSun(d: Date): Date {
  const copy = new Date(d);
  const day = copy.getDay();
  copy.setDate(copy.getDate() + (6 - day));
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function formatMonthDay(d: Date): string {
  return `${MONTH_NAMES_FULL[d.getMonth()]} ${d.getDate()}`;
}

// Mirrors web's calculateProjectedCompletionDate exactly: sums all component
// ETCs across all phases, divides by the user-entered "time per week" form
// answer, projects forward that many weeks, and reports the Saturday of that
// projected week.
function calculateProjectedCompletionDate(project: ProjectData): string | null {
  let totalHours = 0;
  project.phases.forEach((phase) => {
    phase.components.forEach((component) => {
      totalHours += parseEtcToHours(component.etc);
    });
  });
  if (totalHours === 0) return null;

  const timePerWeekStr = project.formAnswers?.[TIME_PER_WEEK_KEY];
  if (!timePerWeekStr) return null;

  const match = timePerWeekStr.match(/(\d+(\.\d+)?)/);
  const hoursPerWeek = match ? parseFloat(match[1]) : 0;
  if (hoursPerWeek <= 0) return null;

  const totalWeeks = Math.ceil(totalHours / hoursPerWeek);
  if (totalWeeks <= 0 || !isFinite(totalWeeks)) return null;

  const futureDate = addWeeks(startOfToday(), totalWeeks);
  const saturday = endOfWeekSun(futureDate);
  return formatMonthDay(saturday);
}

function formatUpdateDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${formatMonthDay(d)}, ${d.getFullYear()}, ${hours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;
}

function getMillis(timestamp?: string): number {
  if (!timestamp) return 0;
  const d = new Date(timestamp);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

// ── Chip / label chooser (shared by header cells + component cells) ──

function LabelPickerSheet({
  visible,
  labels,
  onPick,
  onClear,
  onClose,
  title,
}: {
  visible: boolean;
  labels: ProjectComponentLabel[];
  onPick: (labelId: string) => void;
  onClear: () => void;
  onClose: () => void;
  title: string;
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.sheetCard} onPress={() => {}}>
          <Text style={styles.sheetTitle}>{title}</Text>
          <ScrollView style={{ maxHeight: 320 }}>
            {labels.map((label) => (
              <Pressable key={label.id} style={styles.sheetRow} onPress={() => onPick(label.id)}>
                <View
                  style={[
                    styles.sheetSwatch,
                    { backgroundColor: resolveClassOrHex(label.colorClass, TAILWIND_BG_TO_HEX, colors.glassFill) },
                  ]}
                />
                <Text style={styles.sheetRowText}>{label.name}</Text>
              </Pressable>
            ))}
            {labels.length === 0 ? <Text style={styles.sheetEmpty}>No labels configured in Settings.</Text> : null}
          </ScrollView>
          <Pressable style={styles.sheetClearRow} onPress={onClear}>
            <Text style={styles.sheetClearText}>Clear Label</Text>
          </Pressable>
          <Pressable style={styles.sheetCancelRow} onPress={onClose}>
            <Text style={styles.sheetCancelText}>Cancel</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ── Component cell quick-actions sheet ──

interface CellActionTarget {
  projectId: string;
  phaseIndex: number;
  componentIndex: number;
}

function ComponentActionsSheet({
  visible,
  onClose,
  onEditDetails,
  onApplyLabel,
  onSendToTop,
  onSwapWith,
  onClearCell,
  onInsertRowAbove,
  onInsertRowBelow,
  onInsertColLeft,
  onInsertColRight,
  onDeleteRow,
  onDeleteColumn,
}: {
  visible: boolean;
  onClose: () => void;
  onEditDetails: () => void;
  onApplyLabel: () => void;
  onSendToTop: () => void;
  onSwapWith: () => void;
  onClearCell: () => void;
  onInsertRowAbove: () => void;
  onInsertRowBelow: () => void;
  onInsertColLeft: () => void;
  onInsertColRight: () => void;
  onDeleteRow: () => void;
  onDeleteColumn: () => void;
}) {
  const act = (fn: () => void) => {
    onClose();
    fn();
  };
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.sheetCard} onPress={() => {}}>
          <Text style={styles.sheetTitle}>Component Cell</Text>
          <ScrollView style={{ maxHeight: 420 }}>
            <Pressable style={styles.sheetRow} onPress={() => act(onEditDetails)}>
              <Text style={styles.sheetRowText}>Edit Notes &amp; ETC</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onApplyLabel)}>
              <Text style={styles.sheetRowText}>Apply Label…</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onSendToTop)}>
              <Text style={styles.sheetRowText}>Send to Top</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onSwapWith)}>
              <Text style={styles.sheetRowText}>Swap with…</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onClearCell)}>
              <Text style={styles.sheetRowText}>Clear Cell</Text>
            </Pressable>
            <View style={styles.sheetDivider} />
            <Pressable style={styles.sheetRow} onPress={() => act(onInsertRowAbove)}>
              <Text style={styles.sheetRowText}>Insert Row Above</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onInsertRowBelow)}>
              <Text style={styles.sheetRowText}>Insert Row Below</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onInsertColLeft)}>
              <Text style={styles.sheetRowText}>Insert Column Left</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onInsertColRight)}>
              <Text style={styles.sheetRowText}>Insert Column Right</Text>
            </Pressable>
            <View style={styles.sheetDivider} />
            <Pressable style={styles.sheetRow} onPress={() => act(onDeleteRow)}>
              <Text style={[styles.sheetRowText, styles.sheetRowDanger]}>Delete Row</Text>
            </Pressable>
            <Pressable style={styles.sheetRow} onPress={() => act(onDeleteColumn)}>
              <Text style={[styles.sheetRowText, styles.sheetRowDanger]}>Delete Column</Text>
            </Pressable>
          </ScrollView>
          <Pressable style={styles.sheetCancelRow} onPress={onClose}>
            <Text style={styles.sheetCancelText}>Cancel</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ── Main component ──

export default function ProjectsSection({ userId }: { userId: string | undefined }) {
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [projectComponentLabels, setProjectComponentLabels] = useState<ProjectComponentLabel[]>([]);
  const [defaultProjectComponentLabelId, setDefaultProjectComponentLabelId] = useState<string | null>(null);
  const [projectFormPrompts, setProjectFormPrompts] = useState<ProjectFormPrompt[]>([]);

  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);

  const [notesEditor, setNotesEditor] = useState<{
    projectId: string;
    phaseIndex: number;
    componentIndex: number;
    componentName: string;
    notes: string;
    etc: string;
  } | null>(null);

  const [labelPicker, setLabelPicker] = useState<
    | { kind: 'header'; projectId: string; headerKey: 'topHeaderCell1AppliedLabelId' | 'topHeaderCell2AppliedLabelId' | 'topHeaderCell3AppliedLabelId' | 'topHeaderCell4AppliedLabelId' }
    | { kind: 'component'; projectId: string; phaseIndex: number; componentIndex: number }
    | null
  >(null);

  const [cellActionsTarget, setCellActionsTarget] = useState<CellActionTarget | null>(null);

  // ── Tap-to-swap (touch substitute for web's drag-and-drop component swap) ──
  const [swapSource, setSwapSource] = useState<CellActionTarget | null>(null);

  const [formDialogProjectId, setFormDialogProjectId] = useState<string | null>(null);
  const [formAnswers, setFormAnswers] = useState<Record<string, string>>({});
  const [formStatusUpdates, setFormStatusUpdates] = useState<StatusUpdate[]>([]);
  const [formStatusDraft, setFormStatusDraft] = useState('');
  const [formTaskLists, setFormTaskLists] = useState<TaskListMeta[]>([]);
  const [formSelectedListId, setFormSelectedListId] = useState<string | null>(null);
  const [formLoadingLists, setFormLoadingLists] = useState(false);

  const [milestoneEditorProjectId, setMilestoneEditorProjectId] = useState<string | null>(null);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // ── Load settings (labels, form prompts, default label) ──
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_settings')
        .select('project_component_labels, project_form_prompts, default_project_component_label_id')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      setProjectComponentLabels((data?.project_component_labels ?? []) as ProjectComponentLabel[]);
      setProjectFormPrompts((data?.project_form_prompts ?? []) as ProjectFormPrompt[]);
      setDefaultProjectComponentLabelId(data?.default_project_component_label_id ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ── Load + realtime-subscribe to projects ──
  const load = useCallback(async () => {
    if (!userId) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase.from(SUPABASE_PROJECTS_TABLE).select('*').eq('user_id', userId);
    if (err) {
      setError(err.message);
      setProjects([]);
      setLoading(false);
      return;
    }
    setError(null);

    let fetched: ProjectData[] = (data || []).map(toCamelCase);
    let orderNeedsUpdate = false;
    fetched = fetched
      .sort((a, b) => getMillis(a.createdAt) - getMillis(b.createdAt))
      .map((p, index) => {
        if (p.order === undefined || p.order === null) {
          orderNeedsUpdate = true;
          return { ...p, order: index };
        }
        return p;
      });
    fetched.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (orderNeedsUpdate) {
      try {
        await Promise.all(
          fetched.map((p) =>
            p.id
              ? supabase
                  .from(SUPABASE_PROJECTS_TABLE)
                  .update({ order: p.order, updated_at: new Date().toISOString() })
                  .eq('id', p.id)
              : Promise.resolve()
          )
        );
      } catch (e) {
        // Non-fatal; order will re-normalize on next load.
      }
    }

    setProjects(fetched);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel('mobile-projects-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: SUPABASE_PROJECTS_TABLE, filter: `user_id=eq.${userId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newProject = toCamelCase(payload.new);
            setProjects((prev) => [...prev, newProject].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
          } else if (payload.eventType === 'UPDATE') {
            const updated = toCamelCase(payload.new);
            setProjects((prev) =>
              prev.map((p) => (p.id === updated.id ? updated : p)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as any)?.id;
            if (deletedId) setProjects((prev) => prev.filter((p) => p.id !== deletedId));
          }
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ── Persistence helpers ──

  const saveProject = useCallback(
    async (projectData: ProjectData) => {
      if (!userId || !projectData.id) return Promise.reject(new Error('User or Project ID missing'));

      const { data: currentRow, error: fetchErr } = await supabase
        .from(SUPABASE_PROJECTS_TABLE)
        .select('updated_at')
        .eq('id', projectData.id)
        .single();
      if (fetchErr && (fetchErr as any).code !== 'PGRST116') throw fetchErr;

      if (currentRow) {
        const dbMillis = getMillis(currentRow.updated_at);
        const clientMillis = getMillis(projectData.updatedAt);
        if (dbMillis > clientMillis && clientMillis > 0) {
          setError(`"${projectData.activeTaskSummary}" was updated elsewhere. Refresh before editing.`);
          return Promise.reject(new Error('Conflict detected'));
        }
      }

      const dataToSave: Record<string, any> = toSnakeCase({ ...projectData, userId });
      dataToSave.updated_at = new Date().toISOString();
      if (!currentRow) delete dataToSave.created_at;
      else delete dataToSave.created_at;

      const { error: err } = await supabase.from(SUPABASE_PROJECTS_TABLE).update(dataToSave).eq('id', projectData.id);
      if (err) {
        setError(`Could not save project. ${err.message}`);
        throw err;
      }
      return projectData;
    },
    [userId]
  );

  const updateProjectAndSave = useCallback(
    async (projectId: string, updateFn: (p: ProjectData) => ProjectData) => {
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;
      const updated = updateFn(JSON.parse(JSON.stringify(project)));
      try {
        await saveProject(updated);
      } catch {
        // error already surfaced via saveProject
      }
    },
    [projects, saveProject]
  );

  // Mirrors web's syncSingleHeaderTask — writes/updates a task in the
  // assigned list keyed by project_source {projectId, headerKey}, byte
  // identical column usage so web sees it.
  const syncSingleHeaderTask = useCallback(
    async (project: ProjectData, headerKey: 'topHeaderCell2' | 'topHeaderCell3' | 'topHeaderCell4', headerText: string) => {
      if (!userId || !project.id) return;
      const assignedTaskListId = project.formAnswers?.[ASSIGNED_TASK_LIST_ID_KEY];
      if (!assignedTaskListId || assignedTaskListId === 'none' || assignedTaskListId === '') return;

      const taskTitle = headerText.trim();
      const projectSource = { projectId: project.id, headerKey };

      const { data: existingTasks, error: fetchErr } = await supabase
        .from(SUPABASE_TASKS_TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('list_id', assignedTaskListId)
        .eq('project_source->>projectId', project.id)
        .eq('project_source->>headerKey', headerKey)
        .limit(1);
      if (fetchErr) return;

      const existingTask = existingTasks?.[0];
      if (existingTask) {
        if (existingTask.title !== taskTitle) {
          await supabase
            .from(SUPABASE_TASKS_TABLE)
            .update({ title: taskTitle, updated_at: new Date().toISOString() })
            .eq('id', existingTask.id);
        }
      } else {
        await supabase.from(SUPABASE_TASKS_TABLE).insert({
          user_id: userId,
          list_id: assignedTaskListId,
          title: taskTitle,
          done: false,
          project_source: projectSource,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    },
    [userId]
  );

  // ── Header field editing ──

  const [headerDrafts, setHeaderDrafts] = useState<Record<string, string>>({});

  const headerDraftKey = (projectId: string, field: string) => `${projectId}::${field}`;

  const getHeaderValue = (project: ProjectData, field: 'activeTaskSummary' | 'topHeaderCell2' | 'topHeaderCell3' | 'topHeaderCell4') => {
    const key = headerDraftKey(project.id, field);
    return key in headerDrafts ? headerDrafts[key] : project[field] || '';
  };

  const setHeaderDraft = (projectId: string, field: string, value: string) => {
    setHeaderDrafts((prev) => ({ ...prev, [headerDraftKey(projectId, field)]: value }));
  };

  const commitHeaderField = async (
    project: ProjectData,
    field: 'activeTaskSummary' | 'topHeaderCell2' | 'topHeaderCell3' | 'topHeaderCell4'
  ) => {
    const key = headerDraftKey(project.id, field);
    if (!(key in headerDrafts)) return;
    const value = headerDrafts[key];
    setHeaderDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (!userId) return;

    const fieldMap: Record<string, string> = {
      activeTaskSummary: 'active_task_summary',
      topHeaderCell2: 'top_header_cell2',
      topHeaderCell3: 'top_header_cell3',
      topHeaderCell4: 'top_header_cell4',
    };

    const { error: err } = await supabase
      .from(SUPABASE_PROJECTS_TABLE)
      .update({ [fieldMap[field]]: value, updated_at: new Date().toISOString() })
      .eq('id', project.id);
    if (err) {
      setError(`Failed to save ${field}.`);
      return;
    }

    setProjects((prev) => prev.map((p) => (p.id === project.id ? { ...p, [field]: value } : p)));

    if (field !== 'activeTaskSummary' && project.formAnswers?.[ASSIGNED_TASK_LIST_ID_KEY]) {
      await syncSingleHeaderTask(project, field, value);
    }
  };

  // ── Component name editing ──

  const [componentNameDrafts, setComponentNameDrafts] = useState<Record<string, string>>({});
  const componentDraftKey = (projectId: string, phaseIndex: number, componentIndex: number) =>
    `${projectId}::${phaseIndex}::${componentIndex}`;

  const getComponentName = (projectId: string, phaseIndex: number, componentIndex: number, fallback: string) => {
    const key = componentDraftKey(projectId, phaseIndex, componentIndex);
    return key in componentNameDrafts ? componentNameDrafts[key] : fallback;
  };

  const setComponentNameDraft = (projectId: string, phaseIndex: number, componentIndex: number, value: string) => {
    setComponentNameDrafts((prev) => ({ ...prev, [componentDraftKey(projectId, phaseIndex, componentIndex)]: value }));
  };

  const commitComponentName = async (projectId: string, phaseIndex: number, componentIndex: number) => {
    const key = componentDraftKey(projectId, phaseIndex, componentIndex);
    if (!(key in componentNameDrafts)) return;
    const value = componentNameDrafts[key];
    setComponentNameDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    await updateProjectAndSave(projectId, (p) => {
      const newPhases = p.phases.map((phase, pIdx) =>
        pIdx === phaseIndex
          ? { ...phase, components: phase.components.map((c, cIdx) => (cIdx === componentIndex ? { ...c, name: value } : c)) }
          : phase
      );
      return { ...p, phases: newPhases };
    });
  };

  // ── Status cycling (tap) — native substitute for web's label/status menu ──

  const cycleComponentStatus = (projectId: string, phaseIndex: number, componentIndex: number) => {
    updateProjectAndSave(projectId, (p) => {
      const newPhases = p.phases.map((phase, pIdx) =>
        pIdx === phaseIndex
          ? {
              ...phase,
              components: phase.components.map((c, cIdx) => {
                if (cIdx !== componentIndex) return c;
                if (c.appliedLabelId) return c; // labeled cells don't cycle status
                const currentIdx = STATUS_CYCLE.indexOf(c.status);
                const nextStatus = STATUS_CYCLE[(currentIdx + 1) % STATUS_CYCLE.length];
                return { ...c, status: nextStatus };
              }),
            }
          : phase
      );
      return { ...p, phases: newPhases };
    });
  };

  // ── Header/component label application ──

  const applyHeaderLabel = (
    projectId: string,
    headerKey: 'topHeaderCell1AppliedLabelId' | 'topHeaderCell2AppliedLabelId' | 'topHeaderCell3AppliedLabelId' | 'topHeaderCell4AppliedLabelId',
    labelId: string | null
  ) => {
    updateProjectAndSave(projectId, (p) => ({ ...p, [headerKey]: labelId }));
  };

  const applyComponentLabel = (projectId: string, phaseIndex: number, componentIndex: number, labelId: string | null) => {
    updateProjectAndSave(projectId, (p) => {
      const newPhases = p.phases.map((phase, pIdx) =>
        pIdx === phaseIndex
          ? {
              ...phase,
              components: phase.components.map((c, cIdx) =>
                cIdx === componentIndex ? { ...c, appliedLabelId: labelId, status: labelId ? 'inactive' : c.status } : c
              ),
            }
          : phase
      );
      return { ...p, phases: newPhases };
    });
  };

  // ── Cell clear / structural edits ──

  const clearCell = (projectId: string, phaseIndex: number, componentIndex: number) => {
    updateProjectAndSave(projectId, (p) => {
      const newPhases = p.phases.map((phase, pIdx) =>
        pIdx === phaseIndex
          ? {
              ...phase,
              components: phase.components.map((c, cIdx) =>
                cIdx === componentIndex ? { ...c, name: '', notes: '', etc: '', appliedLabelId: null, status: 'inactive' as ComponentStatus } : c
              ),
            }
          : phase
      );
      return { ...p, phases: newPhases };
    });
  };

  // Mirrors web's handleSendComponentToTop exactly: cascades header cells
  // 2→3→4 down (4 loses its old value, 3 takes 4's old value, 2 takes 3's
  // old value) then places the component's name into header cell 2.
  const sendComponentToTop = (projectId: string, phaseIndex: number, componentIndex: number) => {
    updateProjectAndSave(projectId, (p) => {
      const component = p.phases[phaseIndex]?.components[componentIndex];
      if (!component) return p;
      return {
        ...p,
        topHeaderCell3: p.topHeaderCell4 || '',
        topHeaderCell4: p.topHeaderCell2 || '',
        topHeaderCell2: component.name,
      };
    });
  };

  // Mirrors web's handleComponentDrop exactly: swaps the two full
  // ComponentItem objects (id, name, status, notes, appliedLabelId, etc — the
  // whole cell) between phase/component slots. Web only wires drag targets
  // within a single project's own phases.components grid, never across
  // projects, so this is likewise scoped to one project's own cells.
  const swapComponents = (
    projectId: string,
    dragPhaseIndex: number,
    dragComponentIndex: number,
    dropPhaseIndex: number,
    dropComponentIndex: number
  ) => {
    if (dragPhaseIndex === dropPhaseIndex && dragComponentIndex === dropComponentIndex) return;
    updateProjectAndSave(projectId, (p) => {
      const draggedComponent = p.phases[dragPhaseIndex]?.components[dragComponentIndex];
      const droppedOnComponent = p.phases[dropPhaseIndex]?.components[dropComponentIndex];
      if (!draggedComponent || !droppedOnComponent) return p;
      const newPhases = p.phases.map((phase, pIdx) => {
        if (pIdx === dragPhaseIndex && pIdx === dropPhaseIndex) {
          return {
            ...phase,
            components: phase.components.map((c, cIdx) => {
              if (cIdx === dragComponentIndex) return droppedOnComponent;
              if (cIdx === dropComponentIndex) return draggedComponent;
              return c;
            }),
          };
        }
        if (pIdx === dragPhaseIndex) {
          return { ...phase, components: phase.components.map((c, cIdx) => (cIdx === dragComponentIndex ? droppedOnComponent : c)) };
        }
        if (pIdx === dropPhaseIndex) {
          return { ...phase, components: phase.components.map((c, cIdx) => (cIdx === dropComponentIndex ? draggedComponent : c)) };
        }
        return phase;
      });
      return { ...p, phases: newPhases };
    });
  };

  const structuralChange = async (projectId: string, updateFn: (p: ProjectData) => ProjectData) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const updated = updateFn(JSON.parse(JSON.stringify(project)));
    try {
      await saveProject(updated);
    } catch {
      // surfaced via saveProject
    }
  };

  const insertRowAbove = (projectId: string, phaseIndex: number) => {
    structuralChange(projectId, (p) => {
      const newPhases = [...p.phases];
      const numComponents = p.phases[phaseIndex]?.components.length || p.phases[0]?.components.length || 1;
      const newPhase: PhaseItem = { id: generateId(), components: Array(Math.max(0, numComponents)).fill(null).map(() => createDefaultComponent(defaultProjectComponentLabelId)) };
      newPhases.splice(phaseIndex, 0, newPhase);
      return { ...p, phases: newPhases };
    });
  };

  const insertRowBelow = (projectId: string, phaseIndex: number) => {
    structuralChange(projectId, (p) => {
      const newPhases = [...p.phases];
      const numComponents = p.phases[phaseIndex]?.components.length || p.phases[0]?.components.length || 1;
      const newPhase: PhaseItem = { id: generateId(), components: Array(Math.max(0, numComponents)).fill(null).map(() => createDefaultComponent(defaultProjectComponentLabelId)) };
      newPhases.splice(phaseIndex + 1, 0, newPhase);
      return { ...p, phases: newPhases };
    });
  };

  const deleteRow = (projectId: string, phaseIndex: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (project && project.phases.length <= 1 && project.phases[0]?.components.length > 0) {
      Alert.alert('Action Denied', "Cannot delete the last row if it's the only one with components.");
      return;
    }
    structuralChange(projectId, (p) => ({ ...p, phases: p.phases.filter((_, pIdx) => pIdx !== phaseIndex) }));
  };

  const insertColumnLeft = (projectId: string, componentIndex: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (project && project.phases.length === 0) {
      Alert.alert('Action Denied', 'Add a row first before adding columns.');
      return;
    }
    structuralChange(projectId, (p) => ({
      ...p,
      phases: p.phases.map((phase) => {
        const newComponents = [...phase.components];
        newComponents.splice(componentIndex, 0, createDefaultComponent(defaultProjectComponentLabelId));
        return { ...phase, components: newComponents };
      }),
    }));
  };

  const insertColumnRight = (projectId: string, componentIndex: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (project && project.phases.length === 0) {
      Alert.alert('Action Denied', 'Add a row first before adding columns.');
      return;
    }
    structuralChange(projectId, (p) => ({
      ...p,
      phases: p.phases.map((phase) => {
        const newComponents = [...phase.components];
        newComponents.splice(componentIndex + 1, 0, createDefaultComponent(defaultProjectComponentLabelId));
        return { ...phase, components: newComponents };
      }),
    }));
  };

  const deleteColumn = (projectId: string, componentIndex: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (project && project.phases.length > 0 && project.phases[0]?.components.length <= 1) {
      Alert.alert('Action Denied', 'Cannot delete the last column.');
      return;
    }
    structuralChange(projectId, (p) => ({
      ...p,
      phases: p.phases.map((phase) => ({ ...phase, components: phase.components.filter((_, cIdx) => cIdx !== componentIndex) })),
    }));
  };

  // ── Notes/ETC editor ──

  const openNotesEditor = (projectId: string, phaseIndex: number, componentIndex: number) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const component = project.phases[phaseIndex]?.components[componentIndex];
    if (!component) return;
    setNotesEditor({
      projectId,
      phaseIndex,
      componentIndex,
      componentName: component.name,
      notes: component.notes || '',
      etc: component.etc || '',
    });
  };

  const saveNotesEditor = async () => {
    if (!notesEditor) return;
    const { projectId, phaseIndex, componentIndex, notes, etc } = notesEditor;
    await updateProjectAndSave(projectId, (p) => {
      const newPhases = p.phases.map((phase, pIdx) =>
        pIdx === phaseIndex
          ? { ...phase, components: phase.components.map((c, cIdx) => (cIdx === componentIndex ? { ...c, notes, etc: etc.trim() } : c)) }
          : phase
      );
      return { ...p, phases: newPhases };
    });
    setNotesEditor(null);
  };

  // ── Add / delete project ──

  const addNewProject = async () => {
    if (!userId) return;
    const newOrder = projects.length > 0 ? Math.max(...projects.map((p) => p.order ?? 0)) + 1 : 0;
    const newProjectData = createInitialProjectData(userId, defaultProjectComponentLabelId, newOrder);
    const now = new Date().toISOString();
    const snakeData = toSnakeCase(newProjectData);
    snakeData.user_id = userId;
    snakeData.created_at = now;
    snakeData.updated_at = now;

    const { error: err } = await supabase.from(SUPABASE_PROJECTS_TABLE).insert(snakeData);
    if (err) setError(`Could not add new project. ${err.message}`);
  };

  const confirmDeleteProject = async () => {
    if (!deleteTargetId || !userId) return;
    const projectId = deleteTargetId;
    try {
      const { data: tasksToDelete, error: fetchErr } = await supabase
        .from(SUPABASE_TASKS_TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('project_source->>projectId', projectId);
      if (fetchErr) throw fetchErr;

      const { data: projectData } = await supabase.from(SUPABASE_PROJECTS_TABLE).select('*').eq('id', projectId).single();

      if (tasksToDelete && tasksToDelete.length > 0) {
        await Promise.all(
          tasksToDelete.map((task) =>
            moveToTrash(supabase, userId, {
              sourceComponent: 'Projects',
              itemType: 'task',
              originalTable: 'tasks',
              originalId: task.id,
              itemTitle: task.title || task.name || 'Untitled Task',
              itemData: task,
            })
          )
        );
        const taskIds = tasksToDelete.map((t) => t.id);
        await Promise.all(taskIds.map((id) => supabase.from(SUPABASE_TASKS_TABLE).delete().eq('id', id)));
      }

      if (projectData) {
        await moveToTrash(supabase, userId, {
          sourceComponent: 'Projects',
          itemType: 'project',
          originalTable: 'projects',
          originalId: projectId,
          itemTitle: projectData.name || projectData.title || projectData.active_task_summary || 'Untitled Project',
          itemData: projectData,
        });
      }

      const { error: deleteErr } = await supabase.from(SUPABASE_PROJECTS_TABLE).delete().eq('id', projectId);
      if (deleteErr) throw deleteErr;
    } catch (e: any) {
      setError(`Could not delete project. ${e?.message || 'Unknown error.'}`);
    }
    setDeleteTargetId(null);
  };

  // ── Reordering (▲▼ native substitute for web's drag-and-drop) ──

  const moveProject = async (projectId: string, direction: -1 | 1) => {
    const sorted = [...projects].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = sorted.findIndex((p) => p.id === projectId);
    const swapIdx = idx + direction;
    if (idx === -1 || swapIdx < 0 || swapIdx >= sorted.length) return;

    const reordered = [...sorted];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(swapIdx, 0, moved);
    const withOrder = reordered.map((p, i) => ({ ...p, order: i }));

    setProjects(withOrder);
    try {
      await Promise.all(
        withOrder.map((p) =>
          p.id ? supabase.from(SUPABASE_PROJECTS_TABLE).update({ order: p.order, updated_at: new Date().toISOString() }).eq('id', p.id) : Promise.resolve()
        )
      );
    } catch (e: any) {
      setError(`Could not save project order. ${e?.message || 'Unknown error.'}`);
    }
  };

  // ── Toggles ──

  const toggleTableBody = (projectId: string) => {
    updateProjectAndSave(projectId, (p) => ({ ...p, showTableBody: !p.showTableBody }));
  };

  const toggleMilestones = (projectId: string) => {
    updateProjectAndSave(projectId, (p) => ({ ...p, showMilestones: !(p.showMilestones ?? true) }));
  };

  // ── Project form dialog ──

  const openFormDialog = (project: ProjectData) => {
    const initialAnswers = project.formAnswers || {};
    const currentAnswers: Record<string, string> = {};
    projectFormPrompts.forEach((prompt) => {
      currentAnswers[prompt.id] = initialAnswers[prompt.id] || '';
    });
    currentAnswers[TIME_PER_WEEK_KEY] = initialAnswers[TIME_PER_WEEK_KEY] || '';
    setFormAnswers(currentAnswers);
    setFormSelectedListId(initialAnswers[ASSIGNED_TASK_LIST_ID_KEY] || null);
    setFormStatusUpdates(project.statusUpdates || []);
    setFormStatusDraft('');
    setFormDialogProjectId(project.id);

    if (userId) {
      setFormLoadingLists(true);
      supabase
        .from('task_lists')
        .select('id, name')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })
        .then(({ data }) => {
          setFormTaskLists((data || []) as TaskListMeta[]);
          setFormLoadingLists(false);
        });
    }
  };

  const saveProjectForm = async () => {
    if (!formDialogProjectId || !userId) return;
    const projectBeforeSave = projects.find((p) => p.id === formDialogProjectId);
    if (!projectBeforeSave) {
      setFormDialogProjectId(null);
      return;
    }

    let updatedStatusUpdates = [...formStatusUpdates];
    if (formStatusDraft.trim()) {
      updatedStatusUpdates.push({ id: `su-${Date.now()}`, text: formStatusDraft.trim(), date: new Date().toISOString() });
    }

    const finalFormData: Record<string, string> = { ...formAnswers };
    finalFormData[ASSIGNED_TASK_LIST_ID_KEY] = formSelectedListId || '';

    try {
      await updateProjectAndSave(formDialogProjectId, (p) => ({ ...p, formAnswers: finalFormData, statusUpdates: updatedStatusUpdates }));
    } catch {
      setFormDialogProjectId(null);
      return;
    }

    const selectedListId = finalFormData[ASSIGNED_TASK_LIST_ID_KEY];
    if (!selectedListId || selectedListId === 'none' || selectedListId === '') {
      setFormDialogProjectId(null);
      return;
    }

    const headersToProcess: { headerKey: 'topHeaderCell2' | 'topHeaderCell3' | 'topHeaderCell4'; headerText: string }[] = [
      { headerKey: 'topHeaderCell2', headerText: projectBeforeSave.topHeaderCell2 || '' },
      { headerKey: 'topHeaderCell4', headerText: projectBeforeSave.topHeaderCell4 || '' },
      { headerKey: 'topHeaderCell3', headerText: projectBeforeSave.topHeaderCell3 || '' },
    ];

    for (const { headerKey, headerText } of headersToProcess) {
      const taskTitle = headerText.trim();
      const projectSource = { projectId: projectBeforeSave.id, headerKey };
      const { data: existingTasks, error: fetchErr } = await supabase
        .from(SUPABASE_TASKS_TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('list_id', selectedListId)
        .eq('project_source->>projectId', projectBeforeSave.id)
        .eq('project_source->>headerKey', headerKey)
        .limit(1);
      if (fetchErr) continue;

      const existingTask = existingTasks?.[0];
      if (existingTask) {
        if (existingTask.title !== taskTitle) {
          await supabase.from(SUPABASE_TASKS_TABLE).update({ title: taskTitle, updated_at: new Date().toISOString() }).eq('id', existingTask.id);
        }
      } else {
        await supabase.from(SUPABASE_TASKS_TABLE).insert({
          user_id: userId,
          list_id: selectedListId,
          title: taskTitle,
          done: false,
          project_source: projectSource,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    }

    setFormDialogProjectId(null);
  };

  const unassignTaskList = async (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project || !userId) return;
    const newFormAnswers = { ...project.formAnswers };
    delete newFormAnswers[ASSIGNED_TASK_LIST_ID_KEY];
    await updateProjectAndSave(projectId, (p) => ({ ...p, formAnswers: newFormAnswers }));
  };

  // ── Milestones ──

  const updateMilestones = (projectId: string, milestones: Milestone[]) => {
    updateProjectAndSave(projectId, (p) => ({ ...p, milestones }));
  };

  const MILESTONE_COLORS = ['#3b82f6', '#22c55e', '#ef4444', '#eab308', '#a855f7', '#ec4899', '#f97316', '#06b6d4'];

  const addMilestone = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const existing = project.milestones || [];
    const newMilestone: Milestone = {
      id: generateId(),
      label: 'New Milestone',
      date: new Date().toISOString(),
      color: MILESTONE_COLORS[existing.length % MILESTONE_COLORS.length],
      notes: '',
      isComplete: false,
    };
    updateMilestones(projectId, [...existing, newMilestone]);
  };

  const patchMilestone = (projectId: string, milestoneId: string, patch: Partial<Milestone>) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const existing = project.milestones || [];
    updateMilestones(projectId, existing.map((m) => (m.id === milestoneId ? { ...m, ...patch } : m)));
  };

  const removeMilestone = (projectId: string, milestoneId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const existing = project.milestones || [];
    updateMilestones(projectId, existing.filter((m) => m.id !== milestoneId));
  };

  // ── Derived label lookup helpers ──

  const labelById = useMemo(() => {
    const map: Record<string, ProjectComponentLabel> = {};
    projectComponentLabels.forEach((l) => {
      map[l.id] = l;
    });
    return map;
  }, [projectComponentLabels]);

  const headerCellStyle = (labelId: string | null | undefined) => {
    const label = labelId ? labelById[labelId] : null;
    if (!label) return { backgroundColor: 'rgba(66,80,95,0.06)' };
    return { backgroundColor: resolveClassOrHex(label.colorClass, TAILWIND_BG_TO_HEX, 'rgba(66,80,95,0.06)') };
  };

  const headerTextColor = (labelId: string | null | undefined) => {
    const label = labelId ? labelById[labelId] : null;
    if (!label?.fontColorClass) return colors.text;
    return resolveClassOrHex(label.fontColorClass, TAILWIND_TEXT_TO_HEX, colors.text);
  };

  // ── Render ──

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.blueText} />
      </View>
    );
  }

  if (!userId) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>Please log in to manage projects.</Text>
      </View>
    );
  }

  const sortedProjects = [...projects].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <Text style={styles.title}>Projects</Text>
        <Pressable style={styles.addButton} onPress={() => void addNewProject()}>
          <Text style={styles.addButtonText}>+ New Project</Text>
        </Pressable>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        {sortedProjects.length === 0 ? (
          <View style={styles.emptyCard}>
            <Text style={styles.emptyText}>No projects found. Tap &quot;New Project&quot; to get started.</Text>
          </View>
        ) : (
          sortedProjects.map((project, projectIdx) => {
            const projectedDate = calculateProjectedCompletionDate(project);
            const isExpanded = expandedProjectId === project.id;
            const isListAssigned = !!project.formAnswers?.[ASSIGNED_TASK_LIST_ID_KEY];

            return (
              <View key={project.id} style={styles.projectCard}>
                <View style={styles.projectCardHeaderRow}>
                  <View style={styles.reorderCol}>
                    <Pressable
                      style={[styles.reorderBtn, projectIdx === 0 && styles.reorderBtnDisabled]}
                      disabled={projectIdx === 0}
                      onPress={() => void moveProject(project.id, -1)}
                    >
                      <Text style={styles.reorderBtnText}>▲</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.reorderBtn, projectIdx === sortedProjects.length - 1 && styles.reorderBtnDisabled]}
                      disabled={projectIdx === sortedProjects.length - 1}
                      onPress={() => void moveProject(project.id, 1)}
                    >
                      <Text style={styles.reorderBtnText}>▼</Text>
                    </Pressable>
                  </View>

                  <Pressable
                    style={[styles.headerCell, styles.headerCell1, headerCellStyle(project.topHeaderCell1AppliedLabelId)]}
                    onLongPress={() => setLabelPicker({ kind: 'header', projectId: project.id, headerKey: 'topHeaderCell1AppliedLabelId' })}
                    onPress={() => openFormDialog(project)}
                  >
                    <TextInput
                      style={[styles.headerInput, { color: headerTextColor(project.topHeaderCell1AppliedLabelId) }]}
                      value={getHeaderValue(project, 'activeTaskSummary')}
                      onChangeText={(t) => setHeaderDraft(project.id, 'activeTaskSummary', t)}
                      onBlur={() => void commitHeaderField(project, 'activeTaskSummary')}
                      placeholder="Project title…"
                      placeholderTextColor={colors.slate}
                      multiline
                    />
                  </Pressable>

                  <Pressable style={styles.expandBtn} onPress={() => setExpandedProjectId(isExpanded ? null : project.id)}>
                    <Text style={styles.expandBtnText}>{isExpanded ? '▲' : '▼'}</Text>
                  </Pressable>
                  <Pressable style={styles.deleteBtn} onPress={() => setDeleteTargetId(project.id)}>
                    <Text style={styles.deleteBtnText}>✕</Text>
                  </Pressable>
                </View>

                <View style={styles.headerRow2}>
                  {(['topHeaderCell2', 'topHeaderCell3', 'topHeaderCell4'] as const).map((field) => {
                    const labelKey = `${field}AppliedLabelId` as
                      | 'topHeaderCell2AppliedLabelId'
                      | 'topHeaderCell3AppliedLabelId'
                      | 'topHeaderCell4AppliedLabelId';
                    return (
                      <Pressable
                        key={field}
                        style={[styles.headerCell2, headerCellStyle(project[labelKey])]}
                        onLongPress={() => setLabelPicker({ kind: 'header', projectId: project.id, headerKey: labelKey })}
                      >
                        <TextInput
                          style={[styles.headerInput2, { color: headerTextColor(project[labelKey]) }]}
                          value={getHeaderValue(project, field)}
                          onChangeText={(t) => setHeaderDraft(project.id, field, t)}
                          onBlur={() => void commitHeaderField(project, field)}
                          placeholder="—"
                          placeholderTextColor={colors.slate}
                          multiline
                        />
                      </Pressable>
                    );
                  })}
                </View>

                {projectedDate ? <Text style={styles.projectedText}>Projected completion: {projectedDate}</Text> : null}

                {isExpanded ? (
                  <View style={styles.expandedContent}>
                    <View style={styles.togglesRow}>
                      <Pressable style={styles.toggleChip} onPress={() => toggleTableBody(project.id)}>
                        <Text style={styles.toggleChipText}>{project.showTableBody ? 'Hide Grid' : 'Show Grid'}</Text>
                      </Pressable>
                      <Pressable style={styles.toggleChip} onPress={() => toggleMilestones(project.id)}>
                        <Text style={styles.toggleChipText}>{(project.showMilestones ?? true) ? 'Hide Milestones' : 'Show Milestones'}</Text>
                      </Pressable>
                      <Pressable style={styles.toggleChip} onPress={() => openFormDialog(project)}>
                        <Text style={styles.toggleChipText}>Project Form</Text>
                      </Pressable>
                      {isListAssigned ? (
                        <Pressable style={[styles.toggleChip, styles.toggleChipWarn]} onPress={() => void unassignTaskList(project.id)}>
                          <Text style={styles.toggleChipTextWarn}>Unassign List</Text>
                        </Pressable>
                      ) : null}
                    </View>

                    {project.showTableBody ? (
                      project.phases.length === 0 ? (
                        <Text style={styles.emptyText}>No rows. Add rows/columns via a cell&apos;s quick actions.</Text>
                      ) : (
                        <View style={styles.grid}>
                          {swapSource && swapSource.projectId === project.id ? (
                            <View style={styles.swapBanner}>
                              <Text style={styles.swapBannerText}>Select a cell to swap with — tap to choose, ✕ to cancel</Text>
                              <Pressable style={styles.swapBannerCancel} onPress={() => setSwapSource(null)}>
                                <Text style={styles.swapBannerCancelText}>✕</Text>
                              </Pressable>
                            </View>
                          ) : null}
                          {project.phases.map((phase, phaseIndex) => (
                            <View key={phase.id} style={styles.gridRow}>
                              {phase.components.map((component, componentIndex) => {
                                const label = component.appliedLabelId ? labelById[component.appliedLabelId] : null;
                                const cellBg = label
                                  ? resolveClassOrHex(label.colorClass, TAILWIND_BG_TO_HEX, STATUS_COLORS[component.status].bg)
                                  : STATUS_COLORS[component.status].bg;
                                const cellText = label
                                  ? resolveClassOrHex(label.fontColorClass, TAILWIND_TEXT_TO_HEX, STATUS_COLORS[component.status].text)
                                  : STATUS_COLORS[component.status].text;
                                const isSwapSource =
                                  swapSource &&
                                  swapSource.projectId === project.id &&
                                  swapSource.phaseIndex === phaseIndex &&
                                  swapSource.componentIndex === componentIndex;
                                const handleCellPress = () => {
                                  if (swapSource) {
                                    if (swapSource.projectId !== project.id) return;
                                    if (swapSource.phaseIndex === phaseIndex && swapSource.componentIndex === componentIndex) {
                                      setSwapSource(null);
                                      return;
                                    }
                                    swapComponents(project.id, swapSource.phaseIndex, swapSource.componentIndex, phaseIndex, componentIndex);
                                    setSwapSource(null);
                                    return;
                                  }
                                  cycleComponentStatus(project.id, phaseIndex, componentIndex);
                                };
                                return (
                                  <Pressable
                                    key={component.id}
                                    style={[styles.gridCell, { backgroundColor: cellBg }, isSwapSource && styles.gridCellSwapSource]}
                                    onPress={handleCellPress}
                                    onLongPress={() => setCellActionsTarget({ projectId: project.id, phaseIndex, componentIndex })}
                                  >
                                    {component.etc ? <Text style={styles.gridCellEtc}>{component.etc}</Text> : null}
                                    <TextInput
                                      style={[styles.gridCellInput, { color: cellText }]}
                                      value={getComponentName(project.id, phaseIndex, componentIndex, component.name)}
                                      onChangeText={(t) => setComponentNameDraft(project.id, phaseIndex, componentIndex, t)}
                                      onBlur={() => void commitComponentName(project.id, phaseIndex, componentIndex)}
                                      onFocus={() => {}}
                                      placeholder=""
                                      multiline
                                    />
                                    <Pressable
                                      style={styles.gridCellEditBtn}
                                      onPress={() => openNotesEditor(project.id, phaseIndex, componentIndex)}
                                    >
                                      <Text style={styles.gridCellEditBtnText}>✎</Text>
                                    </Pressable>
                                    {!label ? <Text style={styles.gridCellStatusLabel}>{STATUS_COLORS[component.status].label}</Text> : null}
                                  </Pressable>
                                );
                              })}
                            </View>
                          ))}
                        </View>
                      )
                    ) : null}

                    {(project.showMilestones ?? true) ? (
                      <View style={styles.milestonesBlock}>
                        <View style={styles.milestonesHeaderRow}>
                          <Text style={styles.sectionTitle}>Milestones</Text>
                          <Pressable onPress={() => addMilestone(project.id)}>
                            <Text style={styles.milestonesAddText}>+ Add</Text>
                          </Pressable>
                        </View>
                        {(project.milestones || []).length === 0 ? (
                          <Text style={styles.emptyText}>No milestones yet.</Text>
                        ) : (
                          (project.milestones || []).map((m) => (
                            <View key={m.id} style={styles.milestoneRow}>
                              <View style={[styles.milestoneDot, { backgroundColor: m.color }]} />
                              <TextInput
                                style={styles.milestoneLabelInput}
                                value={m.label}
                                onChangeText={(t) => patchMilestone(project.id, m.id, { label: t })}
                              />
                              <Pressable onPress={() => patchMilestone(project.id, m.id, { isComplete: !m.isComplete })}>
                                <Text style={[styles.milestoneCheck, m.isComplete && styles.milestoneCheckDone]}>
                                  {m.isComplete ? '✓' : '○'}
                                </Text>
                              </Pressable>
                              <Pressable onPress={() => removeMilestone(project.id, m.id)}>
                                <Text style={styles.milestoneDelete}>✕</Text>
                              </Pressable>
                            </View>
                          ))
                        )}
                      </View>
                    ) : null}

                    {(project.statusUpdates || []).length > 0 ? (
                      <View style={styles.statusUpdatesBlock}>
                        <Text style={styles.sectionTitle}>Status Updates</Text>
                        {[...(project.statusUpdates || [])]
                          .reverse()
                          .slice(0, 5)
                          .map((u) => (
                            <View key={u.id} style={styles.statusUpdateRow}>
                              <Text style={styles.statusUpdateDate}>{formatUpdateDate(u.date)}</Text>
                              <Text style={styles.statusUpdateText}>{u.text}</Text>
                            </View>
                          ))}
                      </View>
                    ) : null}
                  </View>
                ) : null}
              </View>
            );
          })
        )}
      </ScrollView>

      {/* ── Notes / ETC editor modal ── */}
      <Modal visible={!!notesEditor} transparent animationType="slide" onRequestClose={() => setNotesEditor(null)}>
        <View style={styles.backdrop}>
          <View style={styles.formCard}>
            <Text style={styles.sheetTitle}>Details for: {notesEditor?.componentName || 'Component'}</Text>
            <Text style={styles.fieldLabel}>ETC</Text>
            <TextInput
              style={styles.input}
              placeholder="e.g., 30m, 1h, 2d"
              placeholderTextColor={colors.slate}
              value={notesEditor?.etc ?? ''}
              onChangeText={(t) => notesEditor && setNotesEditor({ ...notesEditor, etc: t })}
            />
            <Text style={styles.fieldLabel}>Notes</Text>
            <TextInput
              style={[styles.input, styles.notesInput]}
              placeholder="Type your notes here…"
              placeholderTextColor={colors.slate}
              value={notesEditor?.notes ?? ''}
              onChangeText={(t) => notesEditor && setNotesEditor({ ...notesEditor, notes: t })}
              multiline
              textAlignVertical="top"
            />
            <View style={styles.footerRow}>
              <Pressable style={[styles.footerBtn, styles.footerBtnSecondary]} onPress={() => setNotesEditor(null)}>
                <Text style={styles.footerBtnSecondaryText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.footerBtn, styles.footerBtnPrimary]} onPress={() => void saveNotesEditor()}>
                <Text style={styles.footerBtnPrimaryText}>Save Details</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Label picker ── */}
      <LabelPickerSheet
        visible={!!labelPicker}
        labels={projectComponentLabels}
        title={labelPicker?.kind === 'header' ? 'Apply Header Label' : 'Apply Component Label'}
        onClose={() => setLabelPicker(null)}
        onClear={() => {
          if (!labelPicker) return;
          if (labelPicker.kind === 'header') applyHeaderLabel(labelPicker.projectId, labelPicker.headerKey, null);
          else applyComponentLabel(labelPicker.projectId, labelPicker.phaseIndex, labelPicker.componentIndex, null);
          setLabelPicker(null);
        }}
        onPick={(labelId) => {
          if (!labelPicker) return;
          if (labelPicker.kind === 'header') applyHeaderLabel(labelPicker.projectId, labelPicker.headerKey, labelId);
          else applyComponentLabel(labelPicker.projectId, labelPicker.phaseIndex, labelPicker.componentIndex, labelId);
          setLabelPicker(null);
        }}
      />

      {/* ── Component cell quick actions ── */}
      <ComponentActionsSheet
        visible={!!cellActionsTarget}
        onClose={() => setCellActionsTarget(null)}
        onEditDetails={() => cellActionsTarget && openNotesEditor(cellActionsTarget.projectId, cellActionsTarget.phaseIndex, cellActionsTarget.componentIndex)}
        onApplyLabel={() =>
          cellActionsTarget &&
          setLabelPicker({ kind: 'component', projectId: cellActionsTarget.projectId, phaseIndex: cellActionsTarget.phaseIndex, componentIndex: cellActionsTarget.componentIndex })
        }
        onSendToTop={() => cellActionsTarget && sendComponentToTop(cellActionsTarget.projectId, cellActionsTarget.phaseIndex, cellActionsTarget.componentIndex)}
        onSwapWith={() => cellActionsTarget && setSwapSource(cellActionsTarget)}
        onClearCell={() => cellActionsTarget && clearCell(cellActionsTarget.projectId, cellActionsTarget.phaseIndex, cellActionsTarget.componentIndex)}
        onInsertRowAbove={() => cellActionsTarget && insertRowAbove(cellActionsTarget.projectId, cellActionsTarget.phaseIndex)}
        onInsertRowBelow={() => cellActionsTarget && insertRowBelow(cellActionsTarget.projectId, cellActionsTarget.phaseIndex)}
        onInsertColLeft={() => cellActionsTarget && insertColumnLeft(cellActionsTarget.projectId, cellActionsTarget.componentIndex)}
        onInsertColRight={() => cellActionsTarget && insertColumnRight(cellActionsTarget.projectId, cellActionsTarget.componentIndex)}
        onDeleteRow={() =>
          cellActionsTarget &&
          Alert.alert('Delete this row?', 'This permanently removes the row from the project.', [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Delete', style: 'destructive', onPress: () => deleteRow(cellActionsTarget.projectId, cellActionsTarget.phaseIndex) },
          ])
        }
        onDeleteColumn={() =>
          cellActionsTarget &&
          Alert.alert('Delete this column?', 'This permanently removes the column from the project.', [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Delete', style: 'destructive', onPress: () => deleteColumn(cellActionsTarget.projectId, cellActionsTarget.componentIndex) },
          ])
        }
      />

      {/* ── Project form dialog ── */}
      <Modal visible={!!formDialogProjectId} transparent animationType="slide" onRequestClose={() => setFormDialogProjectId(null)}>
        <View style={styles.backdrop}>
          <View style={styles.formCard}>
            <ScrollView style={{ maxHeight: 480 }}>
              <Text style={styles.sheetTitle}>
                Project Form: {projects.find((p) => p.id === formDialogProjectId)?.activeTaskSummary || 'Project'}
              </Text>

              <Text style={styles.fieldLabel}>Status Update</Text>
              <TextInput
                style={[styles.input, styles.notesInput]}
                placeholder="Provide a new status update…"
                placeholderTextColor={colors.slate}
                value={formStatusDraft}
                onChangeText={setFormStatusDraft}
                multiline
                textAlignVertical="top"
              />

              <Text style={styles.fieldLabel}>Time Per Week</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g., 10 hours, 2 sessions"
                placeholderTextColor={colors.slate}
                value={formAnswers[TIME_PER_WEEK_KEY] || ''}
                onChangeText={(t) => setFormAnswers((prev) => ({ ...prev, [TIME_PER_WEEK_KEY]: t }))}
              />

              {projectFormPrompts.map((prompt) => (
                <View key={prompt.id}>
                  <Text style={styles.fieldLabel}>{prompt.text}</Text>
                  <TextInput
                    style={[styles.input, styles.notesInput]}
                    placeholder="Your answer…"
                    placeholderTextColor={colors.slate}
                    value={formAnswers[prompt.id] || ''}
                    onChangeText={(t) => setFormAnswers((prev) => ({ ...prev, [prompt.id]: t }))}
                    multiline
                    textAlignVertical="top"
                  />
                </View>
              ))}
              {projectFormPrompts.length === 0 ? <Text style={styles.emptyText}>No dynamic form questions configured yet.</Text> : null}

              <Text style={styles.fieldLabel}>Assign to List (Optional)</Text>
              {formLoadingLists ? (
                <ActivityIndicator color={colors.blueText} style={{ marginVertical: 8 }} />
              ) : (
                <View style={styles.chipRow}>
                  <Pressable
                    style={[styles.chip, !formSelectedListId && styles.chipActive]}
                    onPress={() => setFormSelectedListId(null)}
                  >
                    <Text style={[styles.chipText, !formSelectedListId && styles.chipTextActive]}>None</Text>
                  </Pressable>
                  {formTaskLists.map((list) => (
                    <Pressable
                      key={list.id}
                      style={[styles.chip, formSelectedListId === list.id && styles.chipActive]}
                      onPress={() => setFormSelectedListId(list.id)}
                    >
                      <Text style={[styles.chipText, formSelectedListId === list.id && styles.chipTextActive]}>{list.name}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
              <Text style={styles.hintText}>If a list is selected, tasks will be created from header cells 2, 3, and 4 upon saving.</Text>

              <Text style={styles.sectionTitle}>Status Update History</Text>
              {formStatusUpdates.length === 0 ? (
                <Text style={styles.emptyText}>No status updates yet.</Text>
              ) : (
                [...formStatusUpdates].reverse().map((u) => (
                  <View key={u.id} style={styles.statusUpdateRow}>
                    <Text style={styles.statusUpdateDate}>{formatUpdateDate(u.date)}</Text>
                    <Text style={styles.statusUpdateText}>{u.text}</Text>
                  </View>
                ))
              )}
            </ScrollView>

            <View style={styles.footerRow}>
              <Pressable style={[styles.footerBtn, styles.footerBtnSecondary]} onPress={() => setFormDialogProjectId(null)}>
                <Text style={styles.footerBtnSecondaryText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.footerBtn, styles.footerBtnPrimary]} onPress={() => void saveProjectForm()}>
                <Text style={styles.footerBtnPrimaryText}>Save Form</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Delete confirmation ── */}
      <Modal visible={!!deleteTargetId} transparent animationType="fade" onRequestClose={() => setDeleteTargetId(null)}>
        <View style={styles.backdrop}>
          <View style={styles.sheetCard}>
            <Text style={styles.sheetTitle}>
              Delete Project: &quot;{projects.find((p) => p.id === deleteTargetId)?.activeTaskSummary || 'this project'}&quot;?
            </Text>
            <Text style={styles.hintText}>
              This removes this project and its data. Tasks previously created from its headers in assigned lists will NOT be automatically deleted,
              but will no longer be linked for updates. This cannot be undone for the project data itself.
            </Text>
            <View style={styles.footerRow}>
              <Pressable style={[styles.footerBtn, styles.footerBtnSecondary]} onPress={() => setDeleteTargetId(null)}>
                <Text style={styles.footerBtnSecondaryText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.footerBtn, styles.footerBtnDanger]} onPress={() => void confirmDeleteProject()}>
                <Text style={styles.footerBtnDangerText}>Delete Project</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  emptyText: { color: colors.slate, fontSize: 13, fontStyle: 'italic' },
  emptyCard: {
    backgroundColor: colors.glassFill,
    borderRadius: radii.tile,
    ...neu.raised,
    padding: 24,
    alignItems: 'center',
  },
  error: { color: colors.danger, fontSize: 12, marginBottom: 6, paddingHorizontal: 12 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, marginBottom: 8 },
  title: { fontSize: 20, fontWeight: '900', color: colors.text, fontFamily: fonts.title, textTransform: 'uppercase' },
  addButton: { backgroundColor: colors.lime, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 8 },
  addButtonText: { fontWeight: '900', color: colors.blueText, fontSize: 12 },

  scroll: { flex: 1 },
  scrollContent: { padding: 12, gap: 12, paddingBottom: 32 },

  projectCard: {
    backgroundColor: colors.glassFill,
    borderRadius: radii.tile,
    ...neu.raised,
    padding: 10,
    gap: 8,
  },
  projectCardHeaderRow: { flexDirection: 'row', alignItems: 'stretch', gap: 6 },
  reorderCol: { justifyContent: 'center', gap: 4 },
  reorderBtn: {
    width: 26,
    height: 22,
    borderRadius: 6,
    backgroundColor: colors.glassFill,
    ...neu.raisedSm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reorderBtnDisabled: { opacity: 0.3 },
  reorderBtnText: { fontSize: 11, fontWeight: '900', color: colors.blueText },

  headerCell: { flex: 1, borderRadius: radii.tile, padding: 8, justifyContent: 'center' },
  headerCell1: { minHeight: 44 },
  headerInput: { fontSize: 15, fontWeight: '800', color: colors.text, padding: 0 },

  expandBtn: { width: 32, alignItems: 'center', justifyContent: 'center' },
  expandBtnText: { fontSize: 14, fontWeight: '900', color: colors.blueText },
  deleteBtn: { width: 28, alignItems: 'center', justifyContent: 'center' },
  deleteBtnText: { fontSize: 14, fontWeight: '900', color: colors.danger },

  headerRow2: { flexDirection: 'row', gap: 6 },
  headerCell2: { flex: 1, borderRadius: 8, padding: 6, minHeight: 36, justifyContent: 'center' },
  headerInput2: { fontSize: 12, color: colors.text, padding: 0, textAlign: 'center' },

  projectedText: { fontSize: 11, color: colors.slate, fontStyle: 'italic' },

  expandedContent: { gap: 10, marginTop: 4 },
  togglesRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  toggleChip: {
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: colors.lime,
    ...neu.raisedSm,
    color: colors.blueText,
  },
  toggleChipText: { fontSize: 11, fontWeight: '700', color: colors.blueText },
  toggleChipWarn: { backgroundColor: 'rgba(192,57,43,0.12)' },
  toggleChipTextWarn: { fontSize: 11, fontWeight: '700', color: colors.danger },

  grid: { gap: 4 },
  gridRow: { flexDirection: 'row', gap: 4 },
  gridCell: {
    flex: 1,
    minHeight: 54,
    borderRadius: 8,
    padding: 6,
    justifyContent: 'center',
    position: 'relative',
  },
  gridCellInput: { fontSize: 12, fontWeight: '600', padding: 0, textAlign: 'center' },
  gridCellEtc: {
    position: 'absolute',
    top: 2,
    right: 2,
    fontSize: 8,
    color: 'rgba(0,0,0,0.5)',
    backgroundColor: 'rgba(0,0,0,0.06)',
    borderRadius: 4,
    paddingHorizontal: 3,
  },
  gridCellEditBtn: { position: 'absolute', bottom: 2, right: 2, padding: 2 },
  gridCellEditBtnText: { fontSize: 10, color: 'rgba(0,0,0,0.4)' },
  gridCellStatusLabel: { fontSize: 8, color: 'rgba(0,0,0,0.4)', textAlign: 'center', marginTop: 2 },
  gridCellSwapSource: { borderWidth: 2, borderColor: colors.blue },

  swapBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: colors.lime,
    ...neu.inset,
  },
  swapBannerText: { flex: 1, fontSize: 11, fontWeight: '700', color: colors.blueText },
  swapBannerCancel: { paddingHorizontal: 6, paddingVertical: 2 },
  swapBannerCancelText: { fontSize: 14, fontWeight: '800', color: colors.blueText },

  milestonesBlock: { gap: 6 },
  milestonesHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontSize: 13, fontWeight: '800', color: colors.text, fontFamily: fonts.title, textTransform: 'uppercase' },
  milestonesAddText: { fontSize: 12, fontWeight: '700', color: colors.blueText },
  milestoneRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 },
  milestoneDot: { width: 10, height: 10, borderRadius: 5 },
  milestoneLabelInput: {
    flex: 1,
    fontSize: 12,
    color: colors.text,
    backgroundColor: colors.inputFill,
    ...neu.insetDeep,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  milestoneCheck: { fontSize: 14, color: colors.slate },
  milestoneCheckDone: { color: '#22c55e' },
  milestoneDelete: { fontSize: 12, color: colors.danger, paddingHorizontal: 4 },

  statusUpdatesBlock: { gap: 6 },
  statusUpdateRow: { paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: 'rgba(66,80,95,0.1)' },
  statusUpdateDate: { fontSize: 10, color: colors.slate, marginBottom: 2 },
  statusUpdateText: { fontSize: 12, color: colors.text },

  backdrop: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'flex-end' },
  sheetCard: {
    backgroundColor: colors.glassFillStrong,
    borderTopLeftRadius: radii.panel,
    borderTopRightRadius: radii.panel,
    ...neu.raisedLg,
    padding: 20,
    paddingBottom: 28,
    maxHeight: '80%',
  },
  sheetTitle: { fontSize: 16, fontWeight: '900', color: colors.text, marginBottom: 10, fontFamily: fonts.title, textTransform: 'uppercase' },
  sheetRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 10 },
  sheetRowText: { fontSize: 14, color: colors.text },
  sheetRowDanger: { color: colors.danger },
  sheetSwatch: { width: 14, height: 14, borderRadius: 4 },
  sheetEmpty: { fontSize: 12, color: colors.slate, fontStyle: 'italic', paddingVertical: 8 },
  sheetDivider: { height: 1, backgroundColor: 'rgba(66,80,95,0.12)', marginVertical: 6 },
  sheetClearRow: { paddingVertical: 10, borderTopWidth: 1, borderTopColor: 'rgba(66,80,95,0.12)', marginTop: 4 },
  sheetClearText: { fontSize: 13, fontWeight: '700', color: colors.danger },
  sheetCancelRow: { paddingVertical: 10, alignItems: 'center' },
  sheetCancelText: { fontSize: 13, fontWeight: '700', color: colors.slate },

  formCard: {
    backgroundColor: colors.glassFillStrong,
    borderTopLeftRadius: radii.panel,
    borderTopRightRadius: radii.panel,
    ...neu.raisedLg,
    padding: 20,
    paddingBottom: 24,
    maxHeight: '90%',
  },
  fieldLabel: { fontFamily: fonts.title,
    fontSize: 11,
    fontWeight: '900',
    color: colors.slate,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 12,
    marginBottom: 4,
  },
  input: {
    backgroundColor: colors.inputFill,
    borderRadius: 14,
    ...neu.insetDeep,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 14,
    color: colors.text,
  },
  notesInput: { minHeight: 70 },
  hintText: { fontSize: 11, color: colors.slate, marginTop: 6 },

  chipRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 6 },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.pill,
    backgroundColor: colors.glassFill,
    ...neu.raisedSm,
  },
  chipActive: { backgroundColor: colors.blue, borderColor: colors.blue },
  chipText: { fontSize: 12, fontWeight: '700', color: colors.slate },
  chipTextActive: { color: colors.white },

  footerRow: { flexDirection: 'row', gap: 8, marginTop: 16 },
  footerBtn: { flex: 1, borderRadius: radii.tile, paddingVertical: 10, alignItems: 'center', justifyContent: 'center' },
  footerBtnSecondary: { backgroundColor: 'rgba(0,0,0,0.06)' },
  footerBtnSecondaryText: { fontWeight: '700', color: colors.text, fontSize: 13 },
  footerBtnDanger: { backgroundColor: colors.danger },
  footerBtnDangerText: { fontWeight: '900', color: colors.white, fontSize: 13 },
  footerBtnPrimary: { backgroundColor: colors.lime },
  footerBtnPrimaryText: { fontWeight: '900', color: colors.blueText, fontSize: 13 },
});
